import {Circle, Fill, Stroke, Style, Text} from 'ol/style';
import {Draw, Select} from 'ol/interaction';
import GeoJSON from "ol/format/GeoJSON";
import VectorLayer from "ol/layer/Vector";
import VectorSource from "ol/source/Vector";
import {never, pointerMove} from 'ol/events/condition';

export default class Annotation {
    constructor() {
        this.mode = null;
        this.map = null;
        this.allSelect = false;
        this.select = new Array();
        this.selected = null;
        this.featureStyleFunction = (feature, resolution) => {
            const color = this.getColorByType(feature.get("type"));
            return new Style({
                image: new Circle({
                    radius: resolution < 0.1 ? 0.35 / resolution : 4,
                    fill: new Fill({color: color}),
                    stroke: new Stroke({color: "#000", width: this.allSelect ? 3 : 1}),
                }),
            });
        };
        this.featureHoverStyle = (feature, resolution) => {
            const color = this.getColorByType(feature.get("type"));
            const style = new Style({
                image: new Circle({
                    radius: resolution < 0.1 ? 0.35 / resolution : 4,
                    fill: new Fill({color: color}),
                    stroke: new Stroke({color: "#000", width: 4}),
                }),
                text: new Text({
                    font: '14px Calibri,sans-serif',
                    offsetY: resolution < 0.1 ? 0.35 / resolution + 15 : 19,
                    fill: new Fill({
                        color: '#fff',
                        width: 10,
                    }),
                    stroke: new Stroke({
                        color: '#000',
                        width: 3,
                    }),
                }),
            });
            style.getText().setText(this.getNameByType(feature.get("type")) + this.getReason(feature.get('reason')));
            return style;
        };
        this.annotationLayer = new VectorLayer({
            id: 'annotation',
            zIndex: 4,
            opacity: 100,
            title: "アノテーション",
            visible: true,
            source: new VectorSource(),
            style: this.featureStyleFunction,
        });
        // this.select = new Select({
        //   // condition: pointerMove,
        //   style: this.modifyStyleFunction,
        // });
        this.modify = null;
        this.draw = new Draw({
            condition: never,
        });
        this.errorTypes = [
            {text: "ホットスポット", value: "cell", color: 'rgba(255, 109, 0, 0.7)'},
            {text: "マルチホットスポット", value: "multicell", color: 'rgba(255, 255, 0, 0.7)'},
            {text: "クラスタバイパス", value: "cluster", color: 'rgba(213, 0, 0, 0.7)'},
            {text: "モジュール異常", value: "module", color: 'rgba(150, 0, 61, 0.7)'},
            {text: "ストリング異常", value: "string", color: 'rgba(64, 0, 124, 0.7)'},
            {text: "ジャンクションボックス過熱", value: "junction", color: 'rgba(76, 175, 80, 0.7)'},
            {text: "ＰＩＤ現象の疑い", value: "pid", color: 'rgba(27, 94, 32, 0.7)'},
            {text: "影", value: "shadow", color: 'rgba(45, 121, 179, 0.7)'},
            {text: "新規", value: "new", color: 'rgba(0, 0, 0, 0.7)'},
        ];
        this.errorReasons = [
            { text: "割れ", value: "crack" },
            { text: "堆積物", value: "sediment" },
            { text: "植生", value: "vegetation" },
        ];
        this.hover = new Select({
            condition: pointerMove,
            layers: [this.annotationLayer],
            style: this.featureHoverStyle,
        });
        this.source = null;
    }

    getColorByType(type) {
        return this.errorTypes.find(x => x.value === type).color;
    }

    getNameByType(type) {
        return this.errorTypes.find(x => x.value === type).text;
    }

    getReason(reason){
        if (reason){
            const errorReason = this.errorReasons.find(x => x.value === reason);
            if (errorReason){
                return `（${errorReason.text}）`
            }
        }
        return '';
    }

    init(map, source) {
        this.map = map;
        this.setSource(source);
        // this.onModifyEnd = onModifyEnd;
    }

    setSource(data) {
        this.map.removeInteraction(this.hover);
        const features = data.panel_abnormality.list.map(item => {
            let airprobe_thermal_image = null;
            let airprobe_color_image = null;
            if (item.airprobe_thermal_image){
                airprobe_thermal_image = item.airprobe_thermal_image.name;
            }
            if (item.airprobe_color_image){
                airprobe_color_image = item.airprobe_color_image.name;
            }
            return {
                type: "Feature",
                id: item.id,
                properties: {
                    type: item.type,
                    reason: item.reason,
                    airprobe_thermal_image: airprobe_thermal_image,
                    airprobe_color_image: airprobe_color_image,
                },
                geometry: {
                    type: "Point",
                    coordinates: item.coordinates,
                },
            }
        });
        const rawSource = {
            type: "FeatureCollection",
            features: features,
        };
        const source = new VectorSource({
            features: new GeoJSON().readFeatures(rawSource),
        });

        this.source = source;

        // Update source
        this.annotationLayer.setSource(source);
        this.map.addInteraction(this.hover);
    }

    setMap(map) {
        this.map = map;
    }

    filter(panelErrorTypes, panelErrorReasons) {
        let features = this.source.getFeatures();
        features = features.filter(function (feature){
            let check = true;
            if (feature.get('type')){
                check = check && panelErrorTypes.findIndex(error => error === feature.get('type')) >= 0;
            }
            else {
                check = check && panelErrorTypes.findIndex(error => error === 'undefined') >= 0;
            }
            if (feature.get('reason')){
                check = check && panelErrorReasons.findIndex(error => error === feature.get('reason')) >= 0;
            }
            else {
                check = check && panelErrorReasons.findIndex(error => error === 'undefined') >= 0;
            }
            return check;
        });
        this.annotationLayer.setSource(new VectorSource({
            features: features
        }));
    }

    setSourceForCompare(data){
        const features = data.panel_abnormality.list.map(item => {
            let airprobe_thermal_image = null;
            let airprobe_color_image = null;
            if (item.airprobe_thermal_image){
                airprobe_thermal_image = item.airprobe_thermal_image.name;
            }
            if (item.airprobe_color_image){
                airprobe_color_image = item.airprobe_color_image.name;
            }
            return {
                type: "Feature",
                id: item.id,
                properties: {
                    type: item.type,
                    reason: item.reason,
                    airprobe_thermal_image: airprobe_thermal_image,
                    airprobe_color_image: airprobe_color_image,
                },
                geometry: {
                    type: "Point",
                    coordinates: item.coordinates,
                },
            }
        });
        const rawSource = {
            type: "FeatureCollection",
            features: features,
        };
        const source = new VectorSource({
            features: new GeoJSON().readFeatures(rawSource),
        });

        this.source = source;

        // Update source
        this.annotationLayer.setSource(source);
    }
}
